import {
  FilterList,
  PlusOne,
  Search,
  SearchOff,
} from "@mui/icons-material";
import CachedIcon from "@mui/icons-material/Cached";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import {
  Backdrop,
  Badge,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getProjectsComplete } from "../../api/projects";
import ManagedAccountSelector from "../Accounts/ManagedAccountSelector";
import { useTranslationHook } from "../../hooks";
import { ProjectComplete } from "../../types/model/project";

interface ProjectsListProps {
  accountId?: number;
  readOnly?: boolean;
}

const ProjectsList = ({ 
  accountId,
  readOnly 
}: ProjectsListProps) => {
  const { t } = useTranslationHook();
  const navigate = useNavigate();

  const [projects, setProjects] = useState<ProjectComplete[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<ProjectComplete[]>([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [selectorOpen, setSelectorOpen] = useState(false);

  const [filters, setFilters] = useState({
    search: "",
    activeOnly: true,
  });

  const [filterAnchorEl, setFilterAnchorEl] =
    useState<HTMLButtonElement | null>(null);

  const isFilterOpen = Boolean(filterAnchorEl);

  const session = JSON.parse(localStorage.getItem("session") || "{}");
  const roles = session.role || [];
  const isAdmin = session.isAdministrator || roles.includes("ADMIN");
  const isManager = session.isManager || roles.includes("ACCOUNT_MANAGER");

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const allProjects = await getProjectsComplete();
      const data = accountId
        ? allProjects.filter((p) => p.projectDetail.project.accountId === accountId)
        : allProjects;

      setProjects(data);
      setFilteredProjects(data);
    } catch {
      enqueueSnackbar(t("admin.projects.project-list-load-error"), { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [accountId]);

  useEffect(() => {
    let data = [...projects];

    if (filters.activeOnly) {
      data = data.filter((p) => !p.projectDetail.project.closed);
    }

    if (filters.search) {
      const value = filters.search.toLowerCase();
      data = data.filter(
        (item) =>
          item.projectDetail.project.name.toLowerCase().includes(value) ||
          (isAdmin || isManager
            ? (item.projectDetail.account.name || "")
                .toLowerCase()
                .includes(value)
            : false) ||
          (item.projectDetail.owner.name || "")
            .toLowerCase()
            .includes(value)
      );
    }

    setFilteredProjects(data);
  }, [filters, projects]);

  const activeFilters = useMemo(() => {
    const list: { key: string; label: string }[] = [];

    if (filters.activeOnly) {
      list.push({ key: "activeOnly", label: t("admin.projects.filter-active-projects") });
    }

    if (filters.search) {
      list.push({ key: "search", label: filters.search });
    }

    return list;
  }, [filters, t]);

  const visibleRows = filteredProjects.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const showAccountColumn = (isAdmin || isManager) && !accountId;

  const handleProjectClick = (accountId: number, projectId: number) => {
    navigate(`/accounts/${accountId}/projects/${projectId}`);
  };

  return (
    <>
      <Backdrop
        open={loading}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
        <Paper sx={{ p: 2, display: "flex", flexDirection: "column", flex: 1 }}>
          
          <Toolbar>
            <Typography sx={{ flex: "1 1 100%" }} variant="h5">
              {t("admin.accounts.account-projects")}
            </Typography>

            <Tooltip title={t("generic.filter")}>
              <IconButton onClick={(e) => setFilterAnchorEl(e.currentTarget)}>
                <Badge badgeContent={activeFilters.length} color="primary">
                  <FilterList color={isFilterOpen ? "primary" : undefined} />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title={t("generic.refresh")}>
              <IconButton onClick={fetchProjects}>
                <CachedIcon />
              </IconButton>
            </Tooltip>

            {isManager && !readOnly && (
              <Tooltip title={t("admin.projects.add-project")}>
                <IconButton
                  onClick={() =>
                    accountId
                      ? navigate(`/accounts/${accountId}/projects/add-project`)
                      : setSelectorOpen(true)
                  }
                >
                  <PlusOne />
                </IconButton>
              </Tooltip>
            )}
          </Toolbar>

          <TextField
            size="small"
            placeholder={t("admin.projects.search-placeholder")}
            value={filters.search}
            onChange={(e) =>
              setFilters({ ...filters, search: e.target.value })
            }
            sx={{ mb: 2 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              },
            }}
          />

          {activeFilters.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
              {activeFilters.map((f) => (
                <Chip
                  key={f.key}
                  label={f.label}
                  onDelete={() =>
                    setFilters({ ...filters, [f.key]: "" })
                  }
                  size="small"
                  variant="outlined"
                />
              ))}
            </Stack>
          )}

          <Popover
            open={isFilterOpen}
            anchorEl={filterAnchorEl}
            onClose={() => setFilterAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            slotProps={{ paper: { sx: { p: 3, width: 250 } } }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {t("generic.filter")}
              </Typography>

              <Chip
                label={t("admin.projects.filter-active-projects")}
                color={filters.activeOnly ? "primary" : "default"}
                onClick={() =>
                  setFilters({
                    ...filters,
                    activeOnly: !filters.activeOnly,
                  })
                }
              />
            </Box>
          </Popover>

          <TableContainer sx={{ flex: 1 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("admin.projects.project-name")}</TableCell>
                  {showAccountColumn && (
                    <TableCell>{t("admin.projects.account-name")}</TableCell>
                  )}
                  <TableCell>{t("admin.projects.owner-name")}</TableCell>
                  <TableCell>{t("admin.projects.start-date")}</TableCell>
                  <TableCell>{t("admin.projects.end-date")}</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {visibleRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", opacity: 0.6 }}>
                        <SearchOff sx={{ fontSize: 64, mb: 2 }} />
                        <Typography variant="h6">
                          {t("projects.no-projects")}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleRows.map((item) => (
                    <TableRow
                      hover
                      key={item.projectDetail.project.id}
                      onClick={() =>
                        handleProjectClick(
                          item.projectDetail.project.accountId,
                          item.projectDetail.project.id
                        )
                      }
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Box display="flex" gap={1.5}>
                          <ContentPasteIcon
                            sx={{
                              color: item.projectDetail.project.closed
                                ? "gray"
                                : "green",
                            }}
                          />
                          {item.projectDetail.project.name}
                        </Box>
                      </TableCell>

                      {showAccountColumn && (
                        <TableCell>
                          {item.projectDetail.account.name ||
                            t("generic.unknown")}
                        </TableCell>
                      )}

                      <TableCell>
                        {item.projectDetail.owner.name ||
                          t("generic.unknown")}
                      </TableCell>

                      <TableCell>
                        {item.projectDetail.project.startDate
                          ? new Intl.DateTimeFormat("pt-PT").format(
                              new Date(item.projectDetail.project.startDate)
                            )
                          : ""}
                      </TableCell>

                      <TableCell>
                        {item.projectDetail.project.endDate
                          ? new Intl.DateTimeFormat("pt-PT").format(
                              new Date(item.projectDetail.project.endDate)
                            )
                          : ""}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={filteredProjects.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number.parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      </Box>

      <ManagedAccountSelector
        open={selectorOpen}
        onCancel={() => setSelectorOpen(false)}
        onSelect={(accountId) => {
          setSelectorOpen(false);
          navigate(`/accounts/${accountId}/projects/add-project`);
        }}
      />
    </>
  );
};

export default ProjectsList;